import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navbar } from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  RefreshCw, ExternalLink, AlertTriangle, Circle, CheckCircle2,
  XCircle, Clock, Calendar, Hash, Power,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function adminHeaders() {
  const k = localStorage.getItem('admin_key') || '';
  return { 'X-Admin-Key': k };
}

function dayKey(iso) {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const dday = new Date(d); dday.setHours(0, 0, 0, 0);
  if (dday.getTime() === today.getTime()) return 'Hoy';
  if (dday.getTime() === tomorrow.getTime()) return 'Mañana';
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' });
}
function fmtCET(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' });
}

const PLATFORM_CHIP = {
  instagram: 'bg-[#FF006E]/20 text-pink-300 border-pink-500/40',
  tiktok: 'bg-black text-zinc-200 border-zinc-600',
  telegram_free: 'bg-[#0088CC]/20 text-sky-300 border-sky-500/40',
  telegram_vip: 'bg-[#FFBE0B]/15 text-amber-300 border-amber-500/40',
};
const PLATFORM_LABEL = {
  instagram: 'IG',
  tiktok: 'TikTok',
  telegram_free: 'TG Free',
  telegram_vip: 'TG VIP',
};
const STATUS_CHIP = {
  scheduled: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  publishing: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  published: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  failed: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  cancelled: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
  blocked_not_approved: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  skipped_tiktok: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
};


function QueueTab() {
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/publisher/queue?hours=48`, { headers: adminHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) { toast.error(`Queue error: ${e.message}`); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const grouped = useMemo(() => {
    const by = {};
    for (const it of data.items || []) {
      const k = dayKey(it.scheduled_at);
      (by[k] ||= []).push(it);
    }
    return by;
  }, [data]);

  async function cancel(id) {
    if (!window.confirm('¿Cancelar esta publicación?')) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/publisher/cancel/${id}`, { method: 'POST', headers: adminHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Cancelada'); load();
    } catch (e) { toast.error(e.message); }
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-zinc-400">{data.count || 0} publicaciones en las próximas 48h</div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="queue-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refrescar
        </Button>
      </div>
      {Object.keys(grouped).length === 0 && (
        <div className="text-zinc-500 text-center py-10 border border-white/5 rounded-lg bg-zinc-900/40">
          Sin publicaciones programadas.
        </div>
      )}
      {Object.entries(grouped).map(([dayLabel, rows]) => (
        <div key={dayLabel} className="mb-6">
          <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-zinc-400">
            <Calendar className="w-3.5 h-3.5" /> {dayLabel} <span className="text-zinc-600">· {rows.length}</span>
          </div>
          <div className="space-y-2">
            {rows.map((it) => (
              <Card key={it.id} className="bg-zinc-900/60 border-white/5">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="text-zinc-200 font-mono text-sm w-20">{fmtCET(it.scheduled_at)}</div>
                  <Badge variant="outline" className={PLATFORM_CHIP[it.platform]}>{PLATFORM_LABEL[it.platform] || it.platform}</Badge>
                  <div className="hidden md:block text-xs text-zinc-400 min-w-[120px]">{it.template} · {it.mood}</div>
                  <div className="w-16 h-28 bg-black rounded overflow-hidden flex-shrink-0">
                    {it.thumbnail_url && (
                      <video src={it.thumbnail_url} preload="metadata" muted className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-zinc-300 truncate">{it.caption_preview || '—'}</div>
                    <div className="text-[10px] text-zinc-500 mt-1">attempts: {it.attempts}</div>
                  </div>
                  <Badge variant="outline" className={STATUS_CHIP[it.status]}>{it.status}</Badge>
                  <Button variant="outline" size="sm" onClick={() => cancel(it.id)} data-testid={`cancel-${it.id}`}
                    className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
                    Cancelar
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


function HistoryTab() {
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [platform, setPlatform] = useState('all');
  const [status, setStatus] = useState('all');
  const [template, setTemplate] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ days: '7' });
      if (platform !== 'all') qs.set('platform', platform);
      if (status !== 'all') qs.set('status', status);
      if (template !== 'all') qs.set('template', template);
      const r = await fetch(`${API_URL}/api/admin/publisher/history?${qs}`, { headers: adminHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) { toast.error(`History error: ${e.message}`); }
    finally { setLoading(false); }
  }, [platform, status, template]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger className="w-40 bg-zinc-900 border-white/10" data-testid="history-platform"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas plataformas</SelectItem>
            <SelectItem value="instagram">Instagram</SelectItem>
            <SelectItem value="tiktok">TikTok</SelectItem>
            <SelectItem value="telegram_free">Telegram Free</SelectItem>
            <SelectItem value="telegram_vip">Telegram VIP</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 bg-zinc-900 border-white/10" data-testid="history-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="blocked_not_approved">Blocked</SelectItem>
            <SelectItem value="skipped_tiktok">Skipped TikTok</SelectItem>
          </SelectContent>
        </Select>
        <Select value={template} onValueChange={setTemplate}>
          <SelectTrigger className="w-44 bg-zinc-900 border-white/10" data-testid="history-template"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos templates</SelectItem>
            {['pick_reveal','win_recap','stats_card','preview_match','meme_fail','countdown'].map(t=>(
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="history-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      {(data.items || []).length === 0 && (
        <div className="text-zinc-500 text-center py-10 border border-white/5 rounded-lg bg-zinc-900/40">Sin registros.</div>
      )}
      <div className="space-y-2">
        {(data.items || []).map((it) => (
          <Card key={it.id} className="bg-zinc-900/60 border-white/5">
            <CardContent className="p-3 flex items-center gap-3 flex-wrap">
              <div className="text-zinc-300 font-mono text-xs w-28">
                {new Date(it.updated_at).toLocaleString('es-ES', { timeZone: 'Europe/Madrid', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </div>
              <Badge variant="outline" className={PLATFORM_CHIP[it.platform]}>{PLATFORM_LABEL[it.platform] || it.platform}</Badge>
              <div className="text-xs text-zinc-400 w-32">{it.template}</div>
              <Badge variant="outline" className={STATUS_CHIP[it.status]}>{it.status}</Badge>
              <div className="flex-1 min-w-0">
                {it.permalink ? (
                  <a href={it.permalink} target="_blank" rel="noreferrer"
                     className="text-sky-300 hover:text-sky-200 inline-flex items-center gap-1 text-sm truncate">
                    {it.permalink} <ExternalLink className="w-3 h-3" />
                  </a>
                ) : it.error_log ? (
                  <span className="text-rose-300 text-xs truncate block" title={it.error_log}>{it.error_log}</span>
                ) : <span className="text-zinc-500 text-xs">—</span>}
              </div>
              {it.latency_ms != null && (
                <span className="text-zinc-400 text-xs"><Clock className="inline w-3 h-3 mr-1" />{it.latency_ms}ms</span>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}


function HealthTab() {
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState(false);
  const [pendingScope, setPendingScope] = useState('meta');
  const [confirmToken, setConfirmToken] = useState(null);
  const [confirmMsg, setConfirmMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/publisher/health`, { headers: adminHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setH(await r.json());
    } catch (e) { toast.error(`Health error: ${e.message}`); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  async function flipAutopublish(scope, enabled) {
    setPendingScope(scope);
    setPendingEnabled(enabled);
    try {
      const r = await fetch(`${API_URL}/api/admin/publisher/flag/autopublish`, {
        method: 'POST', headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled, scope }),
      });
      const d = await r.json();
      setConfirmToken(d.confirm_token);
      setConfirmMsg(d.message || '');
      setConfirmOpen(true);
    } catch (e) { toast.error(e.message); }
  }
  async function confirmFlip() {
    try {
      const r = await fetch(`${API_URL}/api/admin/publisher/flag/autopublish`, {
        method: 'POST', headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: pendingEnabled, scope: pendingScope, confirm_token: confirmToken }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Autopublish ${pendingScope} → ${pendingEnabled ? 'ON' : 'OFF'}`);
      setConfirmOpen(false);
      load();
    } catch (e) { toast.error(e.message); }
  }

  if (!h) return <div className="text-zinc-500 py-6">Cargando…</div>;

  const metaEnabled = h.autopublish?.meta?.enabled ?? h.autopublish?.enabled;
  const tgEnabled = h.autopublish?.telegram?.enabled ?? false;
  const tg = h.telegram || { free: {}, vip: {} };

  return (
    <div className="space-y-5">
      <div className="flex gap-4 flex-wrap">
        {/* Meta */}
        <Card className="bg-zinc-900/60 border-white/5 flex-1 min-w-[260px]">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2">Meta / Instagram</div>
            {h.meta.connected ? (
              <div className="flex items-center gap-2 text-emerald-300">
                <CheckCircle2 className="w-5 h-5" />
                <div>
                  <div className="font-semibold">@{h.meta.ig_username}</div>
                  <div className="text-xs text-zinc-400">expira en {h.meta.days_until_expiry} días</div>
                </div>
              </div>
            ) : (
              <a href={`${API_URL}${h.meta.login_url}`} className="flex items-center gap-2 text-rose-300 hover:text-rose-200">
                <XCircle className="w-5 h-5" /> Desconectado — Conectar
              </a>
            )}
            {h.meta.last_error && <div className="text-xs text-rose-300 mt-2">{h.meta.last_error}</div>}
          </CardContent>
        </Card>

        {/* TikTok placeholder */}
        <Card className="bg-zinc-900/60 border-white/5 flex-1 min-w-[260px]">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2">TikTok</div>
            <div className="flex items-center gap-2 text-zinc-500">
              <Circle className="w-5 h-5" />
              <div>
                <div className="font-semibold">Pendiente</div>
                <div className="text-xs">{h.tiktok.note}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Telegram Free */}
        <Card className="bg-zinc-900/60 border-white/5 flex-1 min-w-[260px]" data-testid="health-telegram-free">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2">
              <span className="inline-block w-2 h-2 rounded-full bg-[#0088CC] mr-2 align-middle" />
              Telegram Free
            </div>
            {tg.free?.connected ? (
              <div className="flex items-center gap-2 text-emerald-300">
                <CheckCircle2 className="w-5 h-5" />
                <div>
                  <div className="font-semibold">@{tg.free?.bot_username}</div>
                  <div className="text-xs text-zinc-400 font-mono">{tg.free?.chat_id}</div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-rose-300">
                <XCircle className="w-5 h-5" />
                <div>
                  <div className="font-semibold">Desconectado</div>
                  <div className="text-xs">{tg.free?.last_error || 'Sin bot token'}</div>
                </div>
              </div>
            )}
            {tg.free?.last_publish && (
              <div className="text-[10px] text-zinc-500 mt-2">
                Último: {tg.free.last_publish.status} · {new Date(tg.free.last_publish.when).toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Telegram VIP */}
        <Card className="bg-zinc-900/60 border-white/5 flex-1 min-w-[260px]" data-testid="health-telegram-vip">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2">
              <span className="inline-block w-2 h-2 rounded-full bg-[#FFBE0B] mr-2 align-middle" />
              Telegram VIP
            </div>
            {tg.vip?.connected ? (
              <div className="flex items-center gap-2 text-emerald-300">
                <CheckCircle2 className="w-5 h-5" />
                <div>
                  <div className="font-semibold">@{tg.vip?.bot_username}</div>
                  <div className="text-xs text-zinc-400 font-mono">{tg.vip?.chat_id}</div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-rose-300">
                <XCircle className="w-5 h-5" />
                <div>
                  <div className="font-semibold">Desconectado</div>
                  <div className="text-xs">{tg.vip?.last_error || 'Sin bot token'}</div>
                </div>
              </div>
            )}
            {tg.vip?.last_publish && (
              <div className="text-[10px] text-zinc-500 mt-2">
                Último: {tg.vip.last_publish.status} · {new Date(tg.vip.last_publish.when).toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 24h counters */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-zinc-900/60 border-white/5"><CardContent className="p-4">
          <div className="text-xs text-zinc-400 uppercase">Publicados 24h</div>
          <div className="text-3xl font-bold text-emerald-300">{h.counters_24h.published}</div>
        </CardContent></Card>
        <Card className="bg-zinc-900/60 border-white/5"><CardContent className="p-4">
          <div className="text-xs text-zinc-400 uppercase">Fallidos 24h</div>
          <div className="text-3xl font-bold text-rose-300">{h.counters_24h.failed}</div>
        </CardContent></Card>
        <Card className="bg-zinc-900/60 border-white/5"><CardContent className="p-4">
          <div className="text-xs text-zinc-400 uppercase">Pending review</div>
          <div className="text-3xl font-bold text-amber-300">{h.counters_24h.pending_review_assets}</div>
        </CardContent></Card>
      </div>

      {h.last_error && (
        <Card className="bg-rose-950/40 border-rose-500/30">
          <CardContent className="p-4">
            <div className="text-xs uppercase text-rose-300 mb-1 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Último error del publisher
            </div>
            <div className="text-xs text-zinc-300 font-mono">
              {h.last_error.when} · {h.last_error.platform} · {h.last_error.error}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dual autopublish flags */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card className={metaEnabled ? "bg-emerald-950/30 border-emerald-500/30" : "bg-rose-950/20 border-rose-500/20"}>
          <CardContent className="p-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-400 mb-1 flex items-center gap-1">
                <Power className="w-3.5 h-3.5" /> Autopublish IG + TikTok
              </div>
              <div className={`text-xl font-bold ${metaEnabled ? 'text-emerald-300' : 'text-rose-300'}`}>
                {metaEnabled ? 'ACTIVADO' : 'DESACTIVADO'}
              </div>
              <div className="text-xs text-zinc-500 mt-1">ENABLE_AUTOPUBLISH · Provider: {h.publisher_provider}</div>
            </div>
            <Button
              className={metaEnabled ? "bg-rose-600 hover:bg-rose-500 text-white" : "bg-emerald-600 hover:bg-emerald-500 text-white"}
              onClick={() => flipAutopublish('meta', !metaEnabled)}
              data-testid="flag-autopublish-meta-btn"
            >
              {metaEnabled ? 'Desactivar' : 'Activar'}
            </Button>
          </CardContent>
        </Card>

        <Card className={tgEnabled ? "bg-emerald-950/30 border-emerald-500/30" : "bg-rose-950/20 border-rose-500/20"}>
          <CardContent className="p-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-400 mb-1 flex items-center gap-1">
                <Power className="w-3.5 h-3.5" /> Autopublish Telegram
              </div>
              <div className={`text-xl font-bold ${tgEnabled ? 'text-emerald-300' : 'text-rose-300'}`}>
                {tgEnabled ? 'ACTIVADO' : 'DESACTIVADO'}
              </div>
              <div className="text-xs text-zinc-500 mt-1">ENABLE_AUTOPUBLISH_TELEGRAM · Free + VIP</div>
            </div>
            <Button
              className={tgEnabled ? "bg-rose-600 hover:bg-rose-500 text-white" : "bg-emerald-600 hover:bg-emerald-500 text-white"}
              onClick={() => flipAutopublish('telegram', !tgEnabled)}
              data-testid="flag-autopublish-telegram-btn"
            >
              {tgEnabled ? 'Desactivar' : 'Activar'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white" data-testid="autopublish-confirm">
          <DialogHeader>
            <DialogTitle>Confirma el cambio</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-300">{confirmMsg || `Vas a ${pendingEnabled ? 'ACTIVAR' : 'DESACTIVAR'} la publicación automática de ${pendingScope}.`}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
            <Button
              className={pendingEnabled ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-rose-600 hover:bg-rose-500 text-white"}
              onClick={confirmFlip}
              data-testid="autopublish-confirm-final"
            >
              Confirmar {pendingEnabled ? 'ACTIVAR' : 'DESACTIVAR'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export default function AdminPublisher() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="pt-20 max-w-6xl mx-auto px-4 sm:px-6 pb-16" data-testid="admin-publisher-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">Publisher</h1>
          <p className="text-zinc-400 text-sm mt-1 flex items-center gap-2">
            <Hash className="w-3.5 h-3.5" /> Cola operativa · historial · salud del sistema
          </p>
        </div>
        <Tabs defaultValue="queue">
          <TabsList className="bg-zinc-900 border border-white/5">
            <TabsTrigger value="queue" data-testid="tab-queue">Cola 48h</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Historial 7d</TabsTrigger>
            <TabsTrigger value="health" data-testid="tab-health">Salud</TabsTrigger>
          </TabsList>
          <TabsContent value="queue" className="mt-5"><QueueTab /></TabsContent>
          <TabsContent value="history" className="mt-5"><HistoryTab /></TabsContent>
          <TabsContent value="health" className="mt-5"><HealthTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
